import React, { memo } from 'react';
import {
  TableCell,
  TableRow,
  Avatar,
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  IconButton
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { User, EditingPharmacy } from '../types';
import type { StaffCount } from '../../../staff';
import { ColumnId } from '../hooks/useTableColumns';
import EditableCell from './EditableCell';
import {
  formatPhoneWithSpaces,
  cleanPhoneForBackend,
  handlePhoneInputChange,
  handlePhoneFocus,
  handlePhoneBlur
} from '../../../profile/utils';

interface UserTableRowProps {
  user: User;
  shouldShowIIN: boolean;
  editingPharmacy: EditingPharmacy | null;
  canEditPharmacyNum: (user: User) => boolean;
  onStartEditingPharmacy: (user: User) => void;
  onSaveNewPharmacyNum: (userId: number, newPharmacyNum: string) => void;
  onPharmacyChange: (newPharmacyNum: string) => void;
  onCancelPharmacyEdit: () => void;
  onSelectUser: (user: User) => void;
  onToggleActivation?: (user: User) => void;
  onTogglePOS?: (user: User) => void;
  onToggleReports?: (user: User) => void;
  onImpersonate?: (user: User) => void;
  /** Оператор управляет статусами, но не этим сотрудником (ранг или зона видимости) */
  isActivationDisabled?: boolean;
  visibleColumnIds?: ColumnId[];
  /** Сотрудники точки (§44) - только у точек в зоне users.staff.view */
  staffCount?: StaffCount;
}

// Очистка строки номера до чистых цифр
export const getPhoneDigits = (phone: string | null | undefined): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

// Проверка: действительно ли номер заполнен (ровно 11 цифр с кодом 7 или 8)
export const isFilledPhone = (phone: string | null | undefined): boolean => {
  if (!phone) return false;
  const digits = getPhoneDigits(phone);
  return digits.length === 11;
};

// Проверка: является ли номер мобильным сотовым номером (поддерживающим WhatsApp)
export const isMobileWhatsAppPhone = (phone: string | null | undefined): boolean => {
  if (!isFilledPhone(phone)) return false;
  const digits = getPhoneDigits(phone);
  // Нормализуем первую цифру: 8 -> 7
  const clean = digits.startsWith('8') ? '7' + digits.slice(1) : digits;
  if (!clean.startsWith('7') || clean.length !== 11) return false;

  // Городские стационарные коды Казахстана (не имеют WhatsApp):
  // 710x-718x (Астана 7172, Караганда 7212, Алматы 727, Шымкент 7252 и т.д.)
  const prefix2 = clean.slice(1, 3);
  if (prefix2.startsWith('1') || prefix2.startsWith('2')) {
    return false;
  }

  // Мобильные коды операторов РК (700-709, 747, 750-751, 760-764, 771-778) и сотовые СНГ (900-999)
  const code3 = clean.slice(1, 4);
  const isKzMobile = /^(70[0-9]|747|75[0-1]|76[0-4]|77[1-8])/.test(code3);
  const isRuMobile = /^9[0-9]{2}/.test(code3);

  return isKzMobile || isRuMobile;
};

// Жесткое форматирование номеров телефонов с префиксом +7 по маске: +7 000 000 00 00
export const formatPhonePlus7 = (phone: string | null | undefined): string => {
  return formatPhoneWithSpaces(phone);
};

export {
  formatPhoneWithSpaces,
  cleanPhoneForBackend,
  handlePhoneInputChange,
  handlePhoneFocus,
  handlePhoneBlur
};

// Цвет бейджа задается у роли в конструкторе ролей; без него - нейтральный
const DEFAULT_ROLE_BADGE = { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' };

const UserTableRow: React.FC<UserTableRowProps> = memo(({
  user,
  shouldShowIIN,
  editingPharmacy,
  canEditPharmacyNum,
  onStartEditingPharmacy,
  onSaveNewPharmacyNum,
  onPharmacyChange,
  onCancelPharmacyEdit,
  onSelectUser,
  onToggleActivation,
  onTogglePOS,
  onToggleReports,
  onImpersonate,
  isActivationDisabled = false,
  visibleColumnIds,
  staffCount
}) => {
  const isInactive = user.deactivation === 1;
  const roleBadge = user.roleBadge || DEFAULT_ROLE_BADGE;
  // Сервер разрешает вход только под сотрудником строго младшего ранга - себя в их числе нет
  const showImpersonate = Boolean(onImpersonate && user.canImpersonate === true);
  const hasActions = Boolean(onToggleActivation || onTogglePOS || onToggleReports || showImpersonate);
  const blockedTitle = 'Сотрудник вне вашей зоны управления';

  // Номера телефонов строго с проверкой реальной заполненности (11 цифр)
  const isUserPhoneFilled = isFilledPhone(user.userphone);
  const isWorkPhoneFilled = isFilledPhone(user.workPhone);

  const formattedPhone = isUserPhoneFilled ? formatPhonePlus7(user.userphone) : null;
  const formattedWorkPhone = isWorkPhoneFilled ? formatPhonePlus7(user.workPhone) : null;

  const isUserPhoneWA = isMobileWhatsAppPhone(user.userphone);
  const isWorkPhoneWA = isMobileWhatsAppPhone(user.workPhone);

  // Корпоративная почта: строго если есть в БД (не придумываем то, чего нет)
  const rawMail = user.mailEmail || user.mailLogin;
  const mailLogin = rawMail ? rawMail.replace(/@zerdegroup\.kz$/i, '') : null;
  const mailAddress = rawMail ? (rawMail.includes('@') ? rawMail : `${mailLogin}@zerdegroup.kz`) : null;

  // Определение эффективного порядка и списка отображаемых колонок
  const effectiveColumnIds: ColumnId[] = visibleColumnIds && visibleColumnIds.length > 0
    ? visibleColumnIds
    : [
      'user',
      'role',
      'pharmacyNum',
      'region',
      'phone',
      'fullName',
      ...(shouldShowIIN ? (['iin'] as ColumnId[]) : []),
      'actions'
    ];

  const renderCell = (colId: ColumnId) => {
    switch (colId) {
      case 'user':
        return (
          <TableCell key="user" sx={{ py: 1, pl: 2, minWidth: '220px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={user.avatarUrl || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: isInactive ? '#94a3b8' : '#1570ef',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    border: '2px solid #fff',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
                  }}
                >
                  {user.fullName ? user.fullName.charAt(0).toUpperCase() : <PersonIcon sx={{ fontSize: 20 }} />}
                </Avatar>
                {/* Индикатор онлайн-статуса */}
                {user.canViewActivity !== false && (
                  <Tooltip
                    title={
                      user.isOnline
                        ? `Онлайн • В разделе: ${user.currentSection || 'Портал'}`
                        : user.lastActivityAt
                          ? `Офлайн • Был в сети: ${new Date(user.lastActivityAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                          : (isInactive ? 'Деактивирован' : 'Офлайн')
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 11,
                        height: 11,
                        borderRadius: '50%',
                        bgcolor: user.isOnline ? '#10b981' : (isInactive ? '#f97316' : '#94a3b8'),
                        border: '2px solid #fff',
                        boxShadow: user.isOnline ? '0 0 6px #10b981' : 'none'
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.2 }}>
                    {user.username}
                  </Typography>
                  {user.canViewActivity !== false && user.isOnline && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        color: '#059669',
                        bgcolor: '#ecfdf5',
                        px: 0.6,
                        py: 0.1,
                        borderRadius: '4px',
                        border: '1px solid #a7f3d0'
                      }}
                    >
                      {user.currentSection || 'В сети'}
                    </Typography>
                  )}
                </Box>
                {mailLogin && mailAddress && (
                  <Box
                    component="a"
                    href={`mailto:${mailAddress}`}
                    onClick={(e) => e.stopPropagation()}
                    title={`Написать на ${mailAddress}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.35,
                      color: '#0284c7',
                      textDecoration: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      lineHeight: 1,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        color: '#0369a1',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <MailOutlineIcon sx={{ fontSize: 12, color: '#0284c7' }} />
                    {mailLogin}
                  </Box>
                )}
              </Box>
            </Box>
          </TableCell>
        );

      case 'role':
        return (
          <TableCell key="role" sx={{ width: '130px', py: 1 }}>
            <Chip
              label={user.roleName || user.role}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                bgcolor: roleBadge.bg,
                color: roleBadge.text,
                border: `1px solid ${roleBadge.border}`,
                borderRadius: '6px'
              }}
            />
            {staffCount && staffCount.total > 0 && (
              <Tooltip title={`Активных: ${staffCount.active}, заблокированных: ${staffCount.total - staffCount.active}`} arrow>
                <Typography sx={{ mt: 0.4, fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                  Сотрудников: {staffCount.total}
                </Typography>
              </Tooltip>
            )}
          </TableCell>
        );

      case 'pharmacyNum':
        return (
          <EditableCell
            key="pharmacyNum"
            value={editingPharmacy?.userId === user.id ? editingPharmacy.newPharmacyNum : user.pharmacyNum}
            isEditing={editingPharmacy?.userId === user.id}
            canEdit={canEditPharmacyNum(user)}
            width="90px"
            type="number"
            onDoubleClick={(e?: any) => {
              if (e && e.stopPropagation) e.stopPropagation();
              onStartEditingPharmacy(user);
            }}
            onSave={(value) => onSaveNewPharmacyNum(user.id, value)}
            onCancel={onCancelPharmacyEdit}
            onChange={onPharmacyChange}
          />
        );

      case 'region':
        return (
          <TableCell key="region" sx={{ width: '130px', py: 1 }}>
            <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }} noWrap>
              {user.regionName || user.cityId || '-'}
            </Typography>
          </TableCell>
        );

      case 'phone':
        return (
          <TableCell key="phone" sx={{ width: '170px', py: 1 }}>
            {formattedPhone ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                <Box
                  component="a"
                  href={isUserPhoneWA ? `https://wa.me/${user.userphone!.replace(/\D/g, '').replace(/^8/, '7')}` : `tel:${formattedPhone}`}
                  target={isUserPhoneWA ? "_blank" : undefined}
                  rel={isUserPhoneWA ? "noopener noreferrer" : undefined}
                  onClick={(e) => e.stopPropagation()}
                  title={isUserPhoneWA ? "Личный номер (WhatsApp)" : "Личный телефон"}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    color: isUserPhoneWA ? '#15803d' : '#1e293b',
                    textDecoration: 'none',
                    fontSize: '0.82rem',
                    fontWeight: isUserPhoneWA ? 600 : 400,
                    p: '1px 4px',
                    borderRadius: 1,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isUserPhoneWA ? '#e8f5e9' : '#f1f5f9',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {isUserPhoneWA ? (
                    <WhatsAppIcon sx={{ fontSize: 15, color: '#25d366' }} />
                  ) : (
                    <PhoneIcon sx={{ fontSize: 13, color: '#64748b' }} />
                  )}
                  {formattedPhone}
                </Box>
                {formattedWorkPhone && formattedWorkPhone !== formattedPhone && (
                  <Box
                    component="a"
                    href={isWorkPhoneWA ? `https://wa.me/${user.workPhone!.replace(/\D/g, '').replace(/^8/, '7')}` : `tel:${formattedWorkPhone}`}
                    target={isWorkPhoneWA ? "_blank" : undefined}
                    rel={isWorkPhoneWA ? "noopener noreferrer" : undefined}
                    onClick={(e) => e.stopPropagation()}
                    title={isWorkPhoneWA ? "Рабочий номер (WhatsApp)" : "Рабочий телефон"}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: isWorkPhoneWA ? '#16a34a' : '#64748b',
                      textDecoration: 'none',
                      fontSize: '0.75rem',
                      fontWeight: isWorkPhoneWA ? 600 : 400,
                      p: '1px 4px',
                      borderRadius: 1,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: isWorkPhoneWA ? '#e8f5e9' : '#f1f5f9',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {isWorkPhoneWA ? (
                      <WhatsAppIcon sx={{ fontSize: 13, color: '#25d366' }} />
                    ) : (
                      <PhoneIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                    )}
                    {formattedWorkPhone} <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>(раб.)</span>
                  </Box>
                )}
              </Box>
            ) : formattedWorkPhone ? (
              <Box
                component="a"
                href={isWorkPhoneWA ? `https://wa.me/${user.workPhone!.replace(/\D/g, '').replace(/^8/, '7')}` : `tel:${formattedWorkPhone}`}
                target={isWorkPhoneWA ? "_blank" : undefined}
                rel={isWorkPhoneWA ? "noopener noreferrer" : undefined}
                onClick={(e) => e.stopPropagation()}
                title={isWorkPhoneWA ? "Рабочий номер (WhatsApp)" : "Рабочий телефон"}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  color: isWorkPhoneWA ? '#15803d' : '#1e293b',
                  textDecoration: 'none',
                  fontSize: '0.82rem',
                  fontWeight: isWorkPhoneWA ? 600 : 400,
                  p: '1px 4px',
                  borderRadius: 1,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isWorkPhoneWA ? '#e8f5e9' : '#f1f5f9',
                    textDecoration: 'underline'
                  }
                }}
              >
                {isWorkPhoneWA ? (
                  <WhatsAppIcon sx={{ fontSize: 15, color: '#25d366' }} />
                ) : (
                  <PhoneIcon sx={{ fontSize: 13, color: '#64748b' }} />
                )}
                {formattedWorkPhone} <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>(раб.)</span>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </TableCell>
        );

      case 'fullName':
        return (
          <TableCell key="fullName" sx={{ minWidth: '160px', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontSize: '0.85rem' }}>
              {user.fullName || '-'}
            </Typography>
          </TableCell>
        );

      case 'iin':
        const isIinMasked = user.canViewIin === false || !shouldShowIIN;
        if (isIinMasked) {
          return (
            <TableCell key="iin" sx={{ width: '130px', py: 1 }}>
              <Tooltip title="Просмотр реального ИИН скрыт правами роли">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#94a3b8' }}>
                  <Typography variant="body2" sx={{ letterSpacing: 1, fontFamily: 'monospace' }}>
                    {user.iin || '••••••••••••'}
                  </Typography>
                  <LockOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            </TableCell>
          );
        }
        return (
          <TableCell
            key="iin"
            sx={{
              width: '130px',
              py: 1,
              fontFamily: 'monospace',
              letterSpacing: 0.5,
              fontSize: '0.85rem',
              filter: 'blur(4px)',
              transition: 'filter 0.25s ease',
              '&:hover': {
                filter: 'none'
              }
            }}
          >
            {user.iin || '-'}
          </TableCell>
        );

      case 'mail':
        return (
          <TableCell key="mail" sx={{ width: '160px', py: 1 }}>
            {mailAddress ? (
              <Box
                component="a"
                href={`mailto:${mailAddress}`}
                onClick={(e) => e.stopPropagation()}
                title={`Написать на ${mailAddress}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  color: '#0284c7',
                  textDecoration: 'none',
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  p: '1px 4px',
                  borderRadius: 1,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: '#f0f9ff',
                    textDecoration: 'underline'
                  }
                }}
              >
                <MailOutlineIcon sx={{ fontSize: 14, color: '#0284c7' }} />
                {mailAddress}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </TableCell>
        );

      case 'address':
        return (
          <TableCell key="address" sx={{ minWidth: '180px', maxWidth: '260px', py: 1 }}>
            <Tooltip title={user.pharmacyAddress || user.address || ''}>
              <Typography
                variant="body2"
                sx={{ color: '#475569', fontSize: '0.85rem' }}
                noWrap
              >
                {user.pharmacyAddress || user.address || '-'}
              </Typography>
            </Tooltip>
          </TableCell>
        );

      case 'workPhone':
        return (
          <TableCell key="workPhone" sx={{ width: '170px', py: 1 }}>
            {formattedWorkPhone ? (
              <Box
                component="a"
                href={isWorkPhoneWA ? `https://wa.me/${user.workPhone!.replace(/\D/g, '').replace(/^8/, '7')}` : `tel:${formattedWorkPhone}`}
                target={isWorkPhoneWA ? "_blank" : undefined}
                rel={isWorkPhoneWA ? "noopener noreferrer" : undefined}
                onClick={(e) => e.stopPropagation()}
                title={isWorkPhoneWA ? "Рабочий номер (WhatsApp)" : "Рабочий телефон"}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  color: isWorkPhoneWA ? '#15803d' : '#1e293b',
                  textDecoration: 'none',
                  fontSize: '0.82rem',
                  fontWeight: isWorkPhoneWA ? 600 : 400,
                  p: '1px 4px',
                  borderRadius: 1,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isWorkPhoneWA ? '#e8f5e9' : '#f1f5f9',
                    textDecoration: 'underline'
                  }
                }}
              >
                {isWorkPhoneWA ? (
                  <WhatsAppIcon sx={{ fontSize: 15, color: '#25d366' }} />
                ) : (
                  <PhoneIcon sx={{ fontSize: 13, color: '#64748b' }} />
                )}
                {formattedWorkPhone}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </TableCell>
        );

      case 'status':
        return (
          <TableCell key="status" sx={{ width: '130px', py: 1 }}>
            <Chip
              size="small"
              label={isInactive ? 'Деактивирован' : 'Активен'}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                bgcolor: isInactive ? '#fff7ed' : '#ecfdf5',
                color: isInactive ? '#c2410c' : '#047857',
                border: `1px solid ${isInactive ? '#fed7aa' : '#a7f3d0'}`,
                borderRadius: '6px'
              }}
            />
          </TableCell>
        );

      case 'posStatus':
        return (
          <TableCell key="posStatus" sx={{ width: '125px', py: 1 }}>
            <Chip
              size="small"
              label={user.pos === 1 ? 'Касса вкл' : 'Касса выкл'}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                bgcolor: user.pos === 1 ? '#ecfdf5' : '#fef2f2',
                color: user.pos === 1 ? '#059669' : '#dc2626',
                border: `1px solid ${user.pos === 1 ? '#a7f3d0' : '#fecaca'}`,
                borderRadius: '6px'
              }}
            />
          </TableCell>
        );

      case 'reportsStatus':
        return (
          <TableCell key="reportsStatus" sx={{ width: '135px', py: 1 }}>
            <Chip
              size="small"
              label={user.reports === 1 ? 'Отчеты вкл' : 'Отчеты выкл'}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                bgcolor: user.reports === 1 ? '#ecfdf5' : '#fef2f2',
                color: user.reports === 1 ? '#059669' : '#dc2626',
                border: `1px solid ${user.reports === 1 ? '#a7f3d0' : '#fecaca'}`,
                borderRadius: '6px'
              }}
            />
          </TableCell>
        );

      case 'mailSession':
        return (
          <TableCell key="mailSession" sx={{ width: '135px', py: 1 }}>
            <Chip
              size="small"
              label={user.hasMailSession ? 'Сессия вкл' : 'Нет сессии'}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                bgcolor: user.hasMailSession ? '#f0fdf4' : '#f8fafc',
                color: user.hasMailSession ? '#15803d' : '#64748b',
                border: `1px solid ${user.hasMailSession ? '#bbf7d0' : '#e2e8f0'}`,
                borderRadius: '6px'
              }}
            />
          </TableCell>
        );

      case 'geolocation': {
        const hasGeo = Boolean(user.latitude && user.longitude);
        return (
          <TableCell key="geolocation" sx={{ width: '150px', py: 1 }}>
            <Tooltip
              title={
                hasGeo
                  ? `${user.latitude}, ${user.longitude}${user.accuracy ? ` (точность ±${user.accuracy}м)` : ''}`
                  : 'GPS-координаты аптеки не зафиксированы'
              }
            >
              <span>
                <Chip
                  size="small"
                  icon={hasGeo ? <LocationOnOutlinedIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                  label={hasGeo ? 'Зафиксирована' : 'Не зафиксир.'}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    height: 24,
                    bgcolor: hasGeo ? '#f0fdf4' : '#f8fafc',
                    color: hasGeo ? '#16a34a' : '#94a3b8',
                    border: `1px solid ${hasGeo ? '#bbf7d0' : '#e2e8f0'}`,
                    borderRadius: '6px'
                  }}
                />
              </span>
            </Tooltip>
          </TableCell>
        );
      }

      case 'geoUpdatedAt':
        return (
          <TableCell key="geoUpdatedAt" sx={{ width: '140px', py: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
              {user.geoUpdatedAt
                ? new Date(user.geoUpdatedAt).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
                : '-'}
            </Typography>
          </TableCell>
        );

      case 'belongsStorage':
        return (
          <TableCell key="belongsStorage" sx={{ width: '160px', py: 1 }}>
            <Tooltip title={user.belongsStorage || user.myPharmacy || ''}>
              <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }} noWrap>
                {user.belongsStorage || user.myPharmacy || '-'}
              </Typography>
            </Tooltip>
          </TableCell>
        );

      case 'id':
        return (
          <TableCell
            key="id"
            sx={{
              width: '80px',
              py: 1,
              fontFamily: 'monospace',
              color: '#64748b',
              fontSize: '0.8rem',
              fontWeight: 500
            }}
          >
            #{user.id}
          </TableCell>
        );

      case 'actions':
        return (
          <TableCell
            key="actions"
            align="right"
            sx={{
              width: '230px',
              py: 1,
              pr: 2.5,
              whiteSpace: 'nowrap'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {hasActions && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                p: '3px',
                bgcolor: '#f1f5f9',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                gap: '4px'
              }}
            >
              {/* [Деакт / Акт] */}
              {onToggleActivation && (
                <Tooltip title={
                  isActivationDisabled
                    ? blockedTitle
                    : user.deactivation === 0
                      ? "Деактивировать аккаунт"
                      : "Активировать аккаунт"
                }>
                  <span>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleActivation(user);
                      }}
                      disabled={isActivationDisabled}
                      sx={{
                        height: 28,
                        minWidth: 54,
                        px: 1,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '6px',
                        bgcolor: user.deactivation === 0 ? '#ffffff' : '#10b981',
                        color: user.deactivation === 0 ? '#d97706' : '#ffffff',
                        border: user.deactivation === 0 ? '1px solid #fde68a' : 'none',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          bgcolor: user.deactivation === 0 ? '#fffbeb' : '#059669',
                          borderColor: user.deactivation === 0 ? '#fcd34d' : 'none'
                        }
                      }}
                    >
                      {user.deactivation === 0 ? 'Деакт' : 'Акт'}
                    </Button>
                  </span>
                </Tooltip>
              )}

              {/* [Касса] */}
              {onTogglePOS && (
                <Tooltip title={
                  isActivationDisabled
                    ? blockedTitle
                    : user.pos === 0
                      ? "Касса заблокирована (клик - включить)"
                      : "Касса активна (клик - отключить)"
                }>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePOS(user);
                      }}
                      disabled={isActivationDisabled}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        bgcolor: user.pos === 1 ? '#ecfdf5' : '#fef2f2',
                        color: user.pos === 1 ? '#059669' : '#dc2626',
                        border: '1px solid',
                        borderColor: user.pos === 1 ? '#a7f3d0' : '#fecaca',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: user.pos === 1 ? '#d1fae5' : '#fee2e2'
                        }
                      }}
                    >
                      <PointOfSaleIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* [Отчеты] */}
              {onToggleReports && (
                <Tooltip title={
                  isActivationDisabled
                    ? blockedTitle
                    : user.pos === 0
                      ? "Сначала разблокируйте кассу"
                      : user.reports === 0
                        ? "Отчеты заблокированы (клик - включить)"
                        : "Отчеты активны (клик - отключить)"
                }>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleReports(user);
                      }}
                      disabled={isActivationDisabled || user.pos === 0}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        bgcolor: user.reports === 1 ? '#ecfdf5' : '#fef2f2',
                        color: user.reports === 1 ? '#059669' : '#dc2626',
                        border: '1px solid',
                        borderColor: user.reports === 1 ? '#a7f3d0' : '#fecaca',
                        opacity: user.pos === 0 ? 0.45 : 1,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: user.reports === 1 ? '#d1fae5' : '#fee2e2'
                        }
                      }}
                    >
                      {user.reports === 0 ? (
                        <LockOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* [Войти под пользователем] */}
              {showImpersonate && onImpersonate && (
                <Tooltip title="Войти под пользователем (проверка интерфейса и прав)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onImpersonate(user);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        bgcolor: '#eef2ff',
                        color: '#4f46e5',
                        border: '1px solid #c7d2fe',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: '#e0e7ff',
                          borderColor: '#818cf8'
                        }
                      }}
                    >
                      <SwitchAccountIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
            )}
          </TableCell>
        );

      default:
        return null;
    }
  };

  return (
    <TableRow
      hover
      onClick={() => onSelectUser(user)}
      sx={{
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'rgba(241, 245, 249, 0.75) !important',
        },
        opacity: isInactive ? 0.65 : 1,
        height: '56px',
        whiteSpace: 'nowrap',
      }}
    >
      {effectiveColumnIds.map(colId => renderCell(colId))}
    </TableRow>
  );
});


UserTableRow.displayName = 'UserTableRow';

export default UserTableRow;
